/**
 * Otto wallpaper themes for Home and Chat. Each theme is a scenic wallpaper
 * plus a matching accent colour. Selecting one overrides the `--o-*` custom
 * properties on <html>, which the Home/Chat CSS reads — so the whole look
 * re-skins with no re-render. The choice is remembered in localStorage.
 *
 * The gold lion brand mark is deliberately NOT themed — it stays constant.
 */

import { followApp, releaseApp } from "./ottoFollow";

export interface OttoTheme {
  id: string;
  name: string;
  file: string; // served from /public; "" means no photo — a plain themed background
  signal: string; // accent, space-separated RGB channels
  signal2: string;
  onSignal: string; // text on an accent fill
  swatch: string; // CSS colour for the picker dot
  /** True when the wallpaper is dark at the top, so text ON it must be light. */
  dark?: boolean;
}

export const OTTO_THEMES: OttoTheme[] = [
  { id: "default", name: "Default", file: "", signal: "5 150 105", signal2: "4 120 87", onSignal: "255 255 255", swatch: "rgb(238 241 246)" },
  { id: "highfell", name: "Highfell", file: "otto-wallpaper.jpg", signal: "45 212 191", signal2: "20 184 166", onSignal: "4 26 22", swatch: "rgb(45 212 191)" },
  { id: "alpenglow", name: "Alpenglow", file: "otto-wp-sunrise.jpg", signal: "251 146 60", signal2: "234 88 12", onSignal: "38 16 2", swatch: "rgb(251 146 60)" },
  { id: "silverwood", name: "Silverwood", file: "otto-wp-forest.jpg", signal: "74 222 128", signal2: "34 197 94", onSignal: "6 26 12", swatch: "rgb(74 222 128)", dark: true },
  { id: "mistfall", name: "Mistfall", file: "otto-wp-mist.jpg", signal: "163 230 53", signal2: "132 204 22", onSignal: "20 26 2", swatch: "rgb(163 230 53)" },
  { id: "nightfall", name: "Nightfall", file: "otto-wp-aurora.jpg", signal: "167 139 250", signal2: "139 92 246", onSignal: "24 14 44", swatch: "rgb(167 139 250)", dark: true },
];

const STORE_KEY = "otto.theme";
export const DEFAULT_THEME_ID = "default";

/** The layered wallpaper value: a top brightening + bottom-tint overlay, the
 *  photo, and a gradient fallback — matching otto.css's own --o-wallpaper.
 *  With no file, it's a plain themed background (no scenic photo). */
function wallpaperValue(file: string): string {
  if (!file) {
    return `linear-gradient(180deg, rgb(250 249 246) 0%, rgb(245 243 238) 100%)`;
  }
  return (
    `linear-gradient(180deg, rgb(255 255 255 / 0.14) 0%, transparent 20%, transparent 70%, rgb(10 13 22 / 0.18) 100%),` +
    ` url("/${file}"),` +
    ` linear-gradient(180deg, rgb(126 180 224) 0%, rgb(160 199 230) 30%, rgb(150 183 120) 62%, rgb(58 96 56) 100%)`
  );
}

export function applyTheme(id: string, persist = true): void {
  const theme = OTTO_THEMES.find((t) => t.id === id) ?? OTTO_THEMES[0];
  const root = document.documentElement.style;
  root.setProperty("--o-wallpaper", wallpaperValue(theme.file));
  root.setProperty("--o-signal", theme.signal);
  root.setProperty("--o-signal-2", theme.signal2);
  root.setProperty("--o-on-signal", theme.onSignal);
  // Text that sits directly on the wallpaper: dark on bright photos, light on
  // dark ones, each with the opposite-tone halo so it reads either way.
  root.setProperty("--o-on-photo", theme.dark ? "237 241 248" : "24 28 42");
  root.setProperty("--o-on-photo-muted", theme.dark ? "205 214 226" : "48 56 74");
  root.setProperty("--o-photo-halo", theme.dark ? "rgb(0 0 0 / 0.6)" : "rgb(255 255 255 / 0.5)");
  // Labels on the wallpaper: a dark glass chip with light text over any photo,
  // plain dark text on the flat Default background.
  const onPhoto = Boolean(theme.file);
  root.setProperty("--o-label-ink", onPhoto ? "237 241 248" : "24 28 42");
  root.setProperty("--o-label-ink-muted", onPhoto ? "205 214 226" : "48 56 74");
  root.setProperty("--o-label-bg", onPhoto ? "rgb(10 13 22 / 0.58)" : "transparent");
  root.setProperty("--o-label-blur", onPhoto ? "10px" : "0px");
  root.setProperty("--o-label-pad", onPhoto ? "3px 10px" : "0px");
  document.documentElement.dataset.ottoTone = onPhoto ? "photo" : "light";
  // No photo: the surface is the app itself, in whatever theme and mode it is in.
  if (onPhoto) releaseApp();
  else followApp();
  if (persist) {
    try {
      localStorage.setItem(STORE_KEY, theme.id);
    } catch {
      /* private window / storage blocked — the theme still applies for this session */
    }
  }
}

export function currentThemeId(): string {
  try {
    return localStorage.getItem(STORE_KEY) ?? DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

/** Apply the remembered theme at startup. Safe to call before mount. */
export function initTheme(): void {
  applyTheme(currentThemeId(), false);
}
